'use client';

import { useEffect, useRef, useState, type RefObject } from 'react';
import { onLateStart } from '@/lib/late-start';

export interface DeferredLoadOptions {
  /** Begin loading this far before the element scrolls into view. */
  rootMargin?: string;
  /** Load anyway after this long, so nothing depends on idle ever arriving. */
  timeoutMs?: number;
}

/**
 * Defers a third-party embed until it is near the viewport, the page has
 * reached its late start (first interaction or 5 s, lib/late-start), and the
 * main thread is free.
 *
 * Two effects, deliberately: an element below the fold (the footer TrustBox)
 * may never load at all, and one above it (the header TrustBox) waits for
 * the late start and idle instead of competing with the page's own first
 * paint (the header TrustBox was ~100 KB and 115-135 ms of main thread on
 * every landing page, Oct 9 2026). Either way the
 * vendor's script leaves the critical path, which is what the lab score and
 * INP are actually measuring.
 */
export function useDeferredLoad<T extends HTMLElement>({
  rootMargin = '300px',
  timeoutMs = 5000,
}: DeferredLoadOptions = {}): [RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return undefined;
    const element = ref.current;
    // No element or no observer support: load rather than hide the widget.
    if (!element || typeof IntersectionObserver === 'undefined') {
      setReady(true);
      return undefined;
    }

    let idleHandle: number | undefined;
    let cancelled = false;
    const whenIdle = () => {
      if (cancelled) return;
      const idle = window.requestIdleCallback;
      idleHandle = idle
        ? idle(() => setReady(true), { timeout: timeoutMs })
        : window.setTimeout(() => setReady(true), 200);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        onLateStart(whenIdle);
      },
      { rootMargin },
    );
    observer.observe(element);

    return () => {
      cancelled = true;
      observer.disconnect();
      if (idleHandle === undefined) return;
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleHandle);
      else window.clearTimeout(idleHandle);
    };
  }, [ready, rootMargin, timeoutMs]);

  return [ref, ready];
}
